import { Component, OnInit } from "@angular/core";
import { PoPageLoginCustomField, PoPageLoginLiterals, PoTemplatesModule } from "@po-ui/ng-templates";
import { AppService } from "../../services/app.service";
import { isValidInput } from "../../utils/input.utils";
import { PoNotificationService } from "@po-ui/ng-components";
import { AuthenticationModel } from "../../models/authentication.model";
import { AuthService } from "../../services/auth.service";
import { SessionService } from "../../services/session.service";
import { ActivatedRoute, Router } from "@angular/router";
import { LocalStorageType } from "../../types/localStorage.type";
import { CommonModule } from "@angular/common";
import { TenantService } from "../../services/tenant.service";
import { ApiService } from "../../services/api.service";
import { GlobalService } from "../../services/global.service";

@Component({
  standalone: true,
  imports: [PoTemplatesModule, CommonModule],
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  constructor(
    private appService: AppService,
    private authService: AuthService,
    private poNotification: PoNotificationService,
    private sessionService: SessionService,
    private router: Router,
    private route: ActivatedRoute,
    private tenantService: TenantService,
    private apiService: ApiService,
    private globalService: GlobalService
  ) {

  }

  ngOnInit(): void {
    this.appService.appTitle ?? this.appTitle;
    this.appService.loginTheme ?? this.theme;

    this.monitorTenantFieldChanges();
    this.monitorTenantFieldValue();
  }

  ngAfterViewInit(): void {
    const inputElement = document.querySelector('input.po-input[name="customFieldInput"]') as HTMLInputElement;
    if (inputElement) {
      inputElement.removeAttribute('disabled');

      this.route.queryParamMap.subscribe(params => {
        const tenantFromUrl = params.get('tenant');
        if (tenantFromUrl && this.isTenantBlock) {
          inputElement.setAttribute('disabled', 'true');
          inputElement.style.borderColor = '#B2B2B2';
          inputElement.style.color = '#B8BCBF';
          inputElement.style.backgroundColor = '#F2F2F2';

          this.getClientConfiguration(tenantFromUrl);
        } else {
          this.theme = this.defaultTheme;
          this.globalService.setClientHost('', '');
        }
      });
    }
  }

  appTitle = 'TOTVS Moda - Portal do Consumidor';

  defaultTheme = 'assets/login_ThemeDefault.png';

  theme = localStorage.getItem(LocalStorageType.themeImage)
    ? `data:image/png;base64,${localStorage.getItem(LocalStorageType.themeImage)}`
    : this.defaultTheme;

  logo = 'assets/login_TotvsLogo.png';

  footerLogo = 'assets/loginFooter_GrayTotvsLogo.png';

  tenant: string | null = '';

  hideTenantField: boolean = false;

  tenantField: PoPageLoginCustomField = {
    property: 'tenant',
    value: this.tenant ?? '',
    placeholder: 'Insira o nome da empresa', //Validar o que será escrito para o usuário!
  };

  literals: PoPageLoginLiterals = {
    welcome: 'Boas-vindas',
    loginPlaceholder: 'Insira seu CPF/CNPJ',
    loginHint: 'Informar o CPF/CNPJ que foi utilizado para se realizar as compras',
  };

  isLoading: boolean = false;
  isTenantBlock: boolean = true;

  async onLogin(formData: any) {
    if (!formData.tenant) {
      formData.tenant = this.tenantService.getTenant();
    }

    formData.login = formData.login.replace(/\D/g, '')

    this.loadingLoginButton();
    this.monitorTenantFieldValue();

    if (!this.isValidFormData(formData))
      return;

    const authenticationModel: AuthenticationModel = {
      tenant: formData.tenant,
      cpfCnpj: formData.login,
      password: formData.password,
      email: '',
      product: 703
    };

    let authenticationResult = await this.authService.authentication(authenticationModel);

    if (!authenticationResult.isAuthenticated) {
      this.refreshLoginButton();
      return;
    }

    if (!await this.sessionService.callback())
      this.router.navigate(['/home']);

    localStorage.setItem(LocalStorageType.Tenant, authenticationModel.tenant);
    localStorage.setItem(LocalStorageType.UserDocument, formData.login);
  }

  private isValidFormData(formData: any): boolean {
    const fieldsNotInformed: Array<string> = [];

    if (!isValidInput(formData.login))
      fieldsNotInformed.push('CPF/CNPJ');

    if (!isValidInput(formData.password))
      fieldsNotInformed.push('Senha');

    if (!isValidInput(formData.tenant))
      fieldsNotInformed.push('Loja');

    if (fieldsNotInformed.length === 0)
      return true;

    this.poNotification.warning(`Campo(s) não informado(s) ou inválido(s): ${fieldsNotInformed.join(', ')}.`);

    return false;
  }

  private loadingLoginButton(): void {
    this.isLoading = true;
  }

  private refreshLoginButton(): void {
    this.isLoading = false;
  }

  monitorTenantFieldValue(): void {
    this.route.queryParamMap.subscribe(params => {
      const tenantFromUrl = params.get('tenant');
      if (tenantFromUrl) {
        this.tenant = tenantFromUrl;
        this.tenantField.value = tenantFromUrl;
        localStorage.setItem(LocalStorageType.UrlTenant, tenantFromUrl);
      }
    });
  }

  monitorTenantFieldChanges(): void {
    setInterval(() => {
      let currentTenantValue = this.tenantField.value;
      if (typeof currentTenantValue === 'number') {
        currentTenantValue = currentTenantValue.toString();
      }

      if (this.tenant !== currentTenantValue) {
        this.tenant = currentTenantValue || '';

        if (this.tenant && this.tenant.trim() !== '') {
          localStorage.setItem(LocalStorageType.UrlTenant, this.tenant);
          this.updateTenantInUrl(this.tenant);
          this.getClientConfiguration(this.tenant);
        }
        else {
          localStorage.removeItem(LocalStorageType.UrlTenant);
          this.removeTenantFromUrl();
        }

        this.isTenantBlock = false;
      }
    }, 500);
  }

  updateTenantInUrl(tenant: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tenant },
      queryParamsHandling: 'merge'
    });
  }

  removeTenantFromUrl(): void {
    this.router.navigate(['/login']);
  }

  getClientConfiguration(tenant: string): void {
    this.apiService.getWebAppData(tenant).subscribe({
      next: (data) => {
        var clientTheme = '';
        if (data.count > 0) {
          if (data.items[0].base64Image) {
            const base64Image = data.items[0].base64Image;
            clientTheme = `data:image/png;base64,${base64Image}`;
            localStorage.setItem(LocalStorageType.themeImage, base64Image);
            this.theme = clientTheme;
          }

          if (data.items[0].urlBase) {
            this.globalService.setClientHost(tenant, data.items[0].urlBase);
          }
        } else {
          localStorage.removeItem(LocalStorageType.themeImage);
          this.globalService.setClientHost('', '');
          this.theme = this.defaultTheme;
        }
      },
      error: (error) => {
        this.poNotification.error('Erro ao buscar dados da empresa.');
      }
    });
  }
}